import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchTasks, createTask, updatePage } from '@/services/redesigns';
import { StitchFlow } from './StitchFlow';
import { FigmaFlow } from './FigmaFlow';
import type { Redesign, RedesignPage, RedesignTask } from '@/types';

interface PageDetailProps {
  redesign: Redesign;
  pageIndex: number;
  onBack: () => void;
}

type ChosenPath = 'stitch' | 'figma' | null;

function derivePathFromTask(task: RedesignTask | null): ChosenPath {
  if (!task) return null;
  if (task.figmaDesign) return 'figma';
  if (task.stitchProjectId || task.status !== 'pending') return 'stitch';
  return null;
}

export function PageDetail({ redesign, pageIndex, onBack }: PageDetailProps) {
  const page = redesign.pages[pageIndex];
  const redesignId = redesign._id;
  const queryClient = useQueryClient();
  const [chosenPath, setChosenPath] = useState<ChosenPath>(null);
  const [fullscreenUrl, setFullscreenUrl] = useState<string | null>(null);
  const [isEditingRoute, setIsEditingRoute] = useState(false);
  const [editRoute, setEditRoute] = useState(page.route);
  const [editViewport, setEditViewport] = useState(page.viewport);

  const routeMutation = useMutation({
    mutationFn: (updates: { route?: string; viewport?: string }) =>
      updatePage(redesignId, pageIndex, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redesign', redesignId] });
      setIsEditingRoute(false);
    },
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ['redesign-tasks', redesignId],
    queryFn: () => fetchTasks(redesignId),
    refetchInterval: (query) => {
      const hasInProgress = query.state.data?.some((t: RedesignTask) => t.status === 'redesigning');
      return hasInProgress ? 5000 : false;
    },
  });

  const task = useMemo(() => {
    return tasks.find(t => t.pageRef.route === page.route && t.pageRef.viewport === page.viewport) ?? null;
  }, [tasks, page.route, page.viewport]);

  // Derive path: local choice takes priority, then infer from task data
  const activePath = chosenPath || derivePathFromTask(task);

  // Route to the correct flow
  if (activePath === 'stitch' && task) {
    return <StitchFlow redesign={redesign} page={page} pageIndex={pageIndex} task={task} onBack={onBack} />;
  }
  if (activePath === 'figma' && task) {
    return <FigmaFlow redesign={redesign} page={page} pageIndex={pageIndex} task={task} onBack={onBack} />;
  }

  // Choice screen
  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14, padding: 0, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 18 }}>&larr;</span> Back to pages
      </button>

      {isEditingRoute ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <input
            autoFocus
            value={editRoute}
            onChange={(e) => setEditRoute(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && editRoute.trim()) {
                routeMutation.mutate({ route: editRoute.trim(), viewport: editViewport });
              } else if (e.key === 'Escape') {
                setIsEditingRoute(false);
                setEditRoute(page.route);
                setEditViewport(page.viewport);
              }
            }}
            placeholder="/page-route"
            style={{
              fontSize: 16, fontWeight: 600, fontFamily: 'monospace',
              padding: '4px 8px', border: '1px solid var(--color-primary)',
              borderRadius: 6, outline: 'none', flex: 1, maxWidth: 300,
            }}
          />
          <select
            value={editViewport}
            onChange={(e) => setEditViewport(e.target.value)}
            style={{
              padding: '4px 8px', border: '1px solid var(--color-border)',
              borderRadius: 6, fontSize: 13,
            }}
          >
            <option value="desktop">Desktop</option>
            <option value="mobile">Mobile</option>
            <option value="tablet">Tablet</option>
          </select>
          <button
            onClick={() => routeMutation.mutate({ route: editRoute.trim(), viewport: editViewport })}
            disabled={!editRoute.trim() || routeMutation.isPending}
            style={{
              padding: '4px 12px', borderRadius: 6, border: 'none',
              background: 'var(--color-primary)', color: '#fff',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {routeMutation.isPending ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={() => { setIsEditingRoute(false); setEditRoute(page.route); setEditViewport(page.viewport); }}
            style={{
              padding: '4px 12px', borderRadius: 6, border: '1px solid var(--color-border)',
              background: 'transparent', color: 'var(--text-muted)',
              fontSize: 12, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
            {page.route} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({page.viewport})</span>
          </h2>
          <button
            onClick={() => { setEditRoute(page.route); setEditViewport(page.viewport); setIsEditingRoute(true); }}
            title="Edit route"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#94a3b8', padding: 2, display: 'flex', alignItems: 'center',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
          </button>
        </div>
      )}
      {page.title && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>{page.title}</div>}

      {page.originalScreenshotUrl && (
        <div style={{ marginBottom: 20 }}>
          <div
            onClick={() => setFullscreenUrl(page.originalScreenshotUrl)}
            style={{ display: 'inline-block', cursor: 'zoom-in', borderRadius: 8, border: '1px solid var(--color-border)', overflow: 'hidden' }}
          >
            <img src={page.originalScreenshotUrl} alt="Current page" style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain', display: 'block' }} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Click to zoom</div>
        </div>
      )}

      {/* Fullscreen overlay */}
      {fullscreenUrl && (
        <div
          onClick={() => setFullscreenUrl(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out', padding: 40,
          }}
        >
          <img src={fullscreenUrl} alt="Full view" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 }} />
          <div style={{ position: 'absolute', top: 20, right: 28, color: 'white', fontSize: 28, fontWeight: 300, cursor: 'pointer' }}>&times;</div>
        </div>
      )}

      <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 16 }}>What would you like to do?</h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <ChoiceCard
          color="var(--color-primary)"
          title="Explore with AI"
          description="Use Gemini + Stitch to analyze the page and generate new design variants with code exports."
          icon="sparkles"
          redesignId={redesignId}
          page={page}
          onChosen={() => setChosenPath('stitch')}
        />
        <ChoiceCard
          color="#7c3aed"
          title="Send to Designer"
          description="Capture the page to Figma as editable frames. A designer reviews and modifies, then hands back."
          icon="figma"
          redesignId={redesignId}
          page={page}
          onChosen={() => setChosenPath('figma')}
        />
      </div>
    </div>
  );
}

function ChoiceCard({ color, title, description, icon, redesignId, page, onChosen }: {
  color: string; title: string; description: string; icon: string;
  redesignId: string; page: RedesignPage; onChosen: () => void;
}) {
  const queryClient = useQueryClient();

  const createTaskMutation = useMutation({
    mutationFn: () => createTask(redesignId, {
      pageRef: { route: page.route, viewport: page.viewport },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redesign-tasks', redesignId] });
      onChosen();
    },
  });

  return (
    <div
      onClick={() => !createTaskMutation.isPending && createTaskMutation.mutate()}
      style={{
        border: `2px solid ${color}20`, borderRadius: 12, padding: 24,
        cursor: createTaskMutation.isPending ? 'wait' : 'pointer',
        background: 'white', transition: 'border-color 150ms, box-shadow 150ms',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 4px 12px ${color}15`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = `${color}20`; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ fontSize: 24, marginBottom: 12 }}>
        {icon === 'sparkles' ? '\u2728' : '\uD83C\uDFA8'}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>{description}</div>
      {createTaskMutation.isPending && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Creating task...</div>
      )}
    </div>
  );
}
